import React, { useEffect, useState, useMemo } from 'react'
import {Timestamp, Span, Paragraph} from 'components/datatable'
import {
  Table, 
  DataTable,
  Button,
  Modal,
  TextArea,
  Checkbox,
  Input
} from '@arnsbogroup/react-voicearchive'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useIntl } from 'react-intl'
import messages from './messages'

export default NiceModal.create(({task, action}) => {
  const modal = useModal()
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [sendCC, setSendCC] = useState(false)
  const [buyouts, setBuyouts] = useState([])
  const { formatMessage, formatDate } = useIntl()
  const domain = window.location.hostname
  const protocol = window.location.protocol

  if (!task) {
    return null
  }

  const acceptConditions = [
    {
      label: formatMessage(messages.deadline),
      value: <Timestamp>{task.deadline}</Timestamp>,
      show: true
    },
    {
      label: formatMessage(messages.budget),
      value: `${!task.cost ? 0 : task.cost} ${task.currency.iso}`,
      show: true
    },
    {
      label: formatMessage(messages.media),
      value: task.media,
      show: !task?.hasUsageTaskSibling
    },
    {
      label: formatMessage(messages.territoryMarket),
      value: task?.territoryMarkets.map(({name}) => name).join(', '),
      show: task?.territoryMarkets && !!task?.territoryMarkets.length && !task?.hasUsageTaskSibling
    },
    {
      label: formatMessage(messages.buyOut),
      value: task?.usage?.name +
        (() => {
          const isInPerpetuity = task?.usage?.slug == 'in_perpetuity'

          if (isInPerpetuity && task?.airing_time_start) {
            return ` (${formatDate(task?.airing_time_start * 1000)} ${formatMessage(messages.onwards)})`
          } else if (task?.airing_time_start && task?.usage?.duration) {
            return ` (${formatDate(task?.airing_time_start * 1000)} - ${formatDate((task?.airing_time_start * 1000) + (task?.usage?.duration * 1000))})`
          }
          return ''
        })(),
      show: !task?.hasUsageTaskSibling
    },
    {
      label: formatMessage(messages.fileFormat),
      value: task.audioFormat,
      show: task.taskType.shortname != 'USAGE'
    },
    {
      label: formatMessage(messages.editingLevel),
      value: task.taskType.shortname == 'VO' && !!task.editingLevel.length ?
        task.editingLevel.map((editing) => (
          <p>
            {editing.name}
          </p>
        )) :
        formatMessage(messages.unknown),
      show: task.taskType.shortname != 'USAGE'
    },
    {
      label: formatMessage(messages.aiDisclaimer),
      value: formatMessage(messages.aiDisclaimerValue),
      show: !['USAGE', 'Ai'].includes(task.taskType.shortname)
    },
    {
      label: formatMessage(messages.fileUpload),
      value: null,
      show: task.taskType.shortname != 'USAGE'
    },
    {
      label: formatMessage(messages.payment),
      value: null,
      show: task.taskType.shortname != 'USAGE'
    },
    {
      label: formatMessage(messages.nda),
      value: null,
      show: task?.taskType?.shortname != 'USAGE'
    },
    {
      label: formatMessage(messages.usageCustomerCancelation),
      value: formatMessage(messages.usageCustomerCancelationValue),
      show: task?.taskType?.shortname == 'USAGE'
    },
    {
      label: formatMessage(messages.usageTerms),
      value: formatMessage(messages.usageTermsValue, {
        a: (chunks) => (
          <a className="text-blue-600 underline" href={`${protocol}//${domain}/docs/Advertising-workflow`} target="_blank">
            {chunks}
          </a>
        )}),
      show: task?.taskType?.shortname == 'USAGE'
    }
  ]

  useEffect(() => {
    if (task.taskType.shortname == 'AU' && !!task.buyouts.length) {
      setBuyouts(task.buyouts.map((buyout) => {
        buyout.editable = buyout.cost == 0 ? true : false
        return buyout
      }))
    }
  }, [])

  const modalMessages = {
    approve: {
      modalTitle: formatMessage(messages.approveModalTitle),
      textAreaLabel: formatMessage(messages.approveTextAreaLabel),
      successToastr: formatMessage(messages.approveSuccessToastr),
      buttonText: formatMessage(messages.approveButtonText),
    },
    deny: {
      modalTitle: formatMessage(messages.denyModalTitle),
      textAreaLabel: formatMessage(messages.denyTextAreaLabel),
      successToastr: formatMessage(messages.denySuccessToastr),
      buttonText: formatMessage(messages.denyButtonText),
    },
    note: {
      modalTitle: formatMessage(messages.noteModalTitle),
      textAreaLabel: formatMessage(messages.noteTextAreaLabel),
      successToastr: formatMessage(messages.noteSuccessToastr),
      buttonText: formatMessage(messages.noteButtonText),
    }
  }[action]

  const onSubmit = () => {
    setLoading(true)
    fetch(`/rest/v2/task/${task.uid}/${action}`, { 
        method: 'PUT',
        body: JSON.stringify({
          message: message,
          getCopy: sendCC
        }) 
      })
      .then((response) => response.json())
      .then((response) => {
        if (!response.success) {
          toastr.error(response.errors.join(', '))
        } else {
          toastr.success(modalMessages.successToastr)
        }
      })
      .catch((error) => toastr.error(error))
      .finally(() => {
        setLoading(false)
        modal.resolve()
        modal.remove()
      })
  }

  const saveBuyouts = () => {
    setLoading(true)
    fetch(`/rest/v2/task/${task.uid}`, { method: 'PUT', body: JSON.stringify({buyouts: buyouts}) })
      .then((response) => response.json())
      .then(({ success, errors }) => {
        if (!success) {
          toastr.error(Object.values(errors).join(', '))
        } else {
          toastr.success(formatMessage(messages.buyoutSaveSuccess))
          onSubmit()
        }
      })
      .then(() => setLoading(false))
      .catch((error) => window.alert(error))
  }

  const onBuyoutChange = (buyoutId, cost) => {
    const updatedBuyouts = buyouts.map((buyout) => {
      if (buyout.uid == buyoutId) {
        return { ...buyout, cost: cost }
      }
      return buyout
    })
    
    setBuyouts(updatedBuyouts)
  }

  return (
    <Modal
      show={modal.visible}
      onClose={() => modal.remove()}
      onOutsideClick={() => modal.remove()}
      onSubmit={() => loading ? undefined : onSubmit()}
      title={`${modalMessages.modalTitle} ${task.title}`}
      dialogPanelClasses="max-w-screen-lg overflow-x-scroll"
    >
      <div className="space-y-4">
        {action == "approve" && 
          <>
            <DataTable
              components={{
                Tr: ({ childProps = {}, ...innerProps }) => {
                  return (
                    <Table.Tr
                      {...innerProps }
                    />
                  )
                },
                Td: ({ childProps = {}, ...props }) => (
                  <Table.Td
                    {...props }
                  />
                )
              }}
              head={{
                label: { sortable: true, label: 'Condition' },
                value: { label: 'Criteria' }
              }}
              body={{
                label: Span,
                value: ({value}) => <Paragraph className="whitespace-pre-wrap">{value}</Paragraph>,
              }}
              entries={acceptConditions.filter(({ show }) => show)}
            />
            {task.taskType.shortname == 'AU' && !!buyouts.length && 
              <>
                <p className='font-medium text-red-400'>
                  {formatMessage(messages.buyoutsMessage)}
                </p>
                <DataTable
                  head={{
                    quantity: { label: formatMessage(messages.buyoutTableQuantity) },
                    ordertargetmedia: { label: formatMessage(messages.buyoutTableMedia) },
                    territoryMarket: { label: formatMessage(messages.territoryMarket) },
                    orderusage: { label: formatMessage(messages.buyoutTableUsage) },
                    cost: { label: formatMessage(messages.buyoutTablePrice) },
                  }}
                  body={{
                    quantity: (entry) =>  <Span>{entry.quantity} {entry.quantity > 1 ? entry.quantity_type.name + 's' : entry.quantity_type.name}</Span>,
                    ordertargetmedia: (entry) =>  <Span>{entry.ordertargetmedia.name}</Span>,
                    orderusage: (entry) =>  <Span>{entry.orderusage.name}</Span>,
                    territoryMarket: (entry) =>  <Span>{entry.territoryMarket.name}</Span>,
                    cost: (entry) => entry.editable ? 
                      <div className='flex flex-col-2 gap-1'>
                        <Input
                          size="sm"
                          type="number"
                          defaultValue={entry.cost}
                          onBlur={({ target: { value } }) => onBuyoutChange(entry.uid, value)}
                        />
                        <div className='my-auto'>
                          <Span>{entry.currency.iso} {formatMessage(messages.total)}</Span>
                        </div>
                      </div> :
                      <Span>{entry.cost} {entry.currency.iso} {formatMessage(messages.total)}</Span>
                  }}
                  entries={buyouts}
                />
              </>
            }
          </>
        }
        <TextArea
          label={modalMessages.textAreaLabel}
          rows={6}
          value={message}
          onChange={({ target: { value } }) => setMessage(value)}
        />
        <Checkbox
          defaultValue={sendCC}
          onChange={() => {
            setSendCC(() => !sendCC)
          }}
          checked={sendCC}
          label="Send to myself as CC"
        />
      </div>
      <div className='text-right'>
        <Button disabled={buyouts.some(({cost}) => !+cost) && action == 'approve'} onClick={() => buyouts.some(({editable}) => editable) ? saveBuyouts() : onSubmit()}>
          {loading ? 'Loading' : modalMessages.buttonText}
        </Button>
      </div>
    </Modal>
  )
})
